import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import FuseUtils from '@fuse/utils';
import Collapse from '@material-ui/core/Collapse';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo, cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import { ListItemIcon } from '@material-ui/core';
import { withRouter } from 'utils/withRouter';
import FuseNavBadge from '../FuseNavBadge';
import FuseNavItem from '../FuseNavItem';

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0
  },
  item: props => ({
    height: 40,
    width: 'calc(100% - 16px)',
    borderRadius: '0 20px 20px 0',
    paddingRight: 12,
    paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
    color: theme.palette.text.primary,
    '& .list-item-icon': {
      marginRight: 16
    }
  })
}));

function needsToBeOpened(location, item) {
  return location && isUrlInChildren(item, location.pathname);
}

function isUrlInChildren(parent, url) {
  if (!parent.children) {
    return false;
  }

  for (let i = 0; i < parent.children.length; i += 1) {
    if (parent.children[i].children) {
      if (isUrlInChildren(parent.children[i], url)) {
        return true;
      }
    }

    if (
      parent.children[i].url === url ||
      url.includes(parent.children[i].url)
    ) {
      return true;
    }
  }

  return false;
}

function FuseNavVerticalCollapse(props) {
  const user = useSelector(({ auth }) => auth.user);
  const [open, setOpen] = useState(() =>
    needsToBeOpened(props.location, props.item)
  );
  const { item, nestedLevel } = props;
  const classes = useStyles({
    itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24
  });
  const { t } = useTranslation('navigation');

  useEffect(() => {
    if (needsToBeOpened(props.location, props.item)) {
      if (!open) {
        setOpen(true);
      }
    }
    // eslint-disable-next-line
  }, [props.location, props.item]);

  function handleClick() {
    setOpen(!open);
  }

  const hasPermission = useMemo(
    () => FuseUtils.hasPermission(item.auth, user.role),
    [item.auth, user]
  );

  const hasOptionalPermission = useMemo(
    () =>
      FuseUtils.hasOptionalItemPermission(
        item.group,
        item.allowedHairdresserTypes,
        user
      ),
    [item.group, item.allowedHairdresserTypes, user]
  );

  if (
    !hasPermission ||
    !hasOptionalPermission ||
    (item.display !== null && item.display !== user.myGroup?.display_type)
  ) {
    return null;
  }

  return (
    <ul className={clsx(classes.root, open && 'open')}>
      <ListItem
        button
        className={clsx(classes.item, 'list-item')}
        onClick={handleClick}
        component={item.url ? NavLinkAdapter : 'li'}
        to={item.url}
        role="button"
      >
        {item.icon && (
          <ListItemIcon>
            {cloneElement(item.icon, {
              ref: node => {
                // Call the original ref, if any
                const { ref } = item.icon;
                if (typeof ref === 'function') {
                  ref(node);
                }
              },
              color: 'action',
              className: 'list-item-icon text-30 flex-shrink-0'
            })}
          </ListItemIcon>
        )}
        <ListItemText
          className="list-item-text"
          primary={item.translate ? t(item.translate) : item.title}
          classes={{ primary: 'text-14' }}
        />

        {item.badge && <FuseNavBadge className="mx-4" badge={item.badge} />}

        <IconButton
          disableRipple
          className="w-40 h-40 -mx-12 p-0 focus:bg-transparent hover:bg-transparent"
          onClick={ev => ev.preventDefault()}
        >
          {open ? (
            <ExpandLessIcon className="text-16" />
          ) : (
            <ExpandMoreIcon className="text-16" />
          )}
        </IconButton>
      </ListItem>

      {item.children && (
        <Collapse in={open} className="collapse-children">
          {item.children.map(_item => (
            <FuseNavItem
              key={_item.id}
              type={`vertical-${_item.type}`}
              item={_item}
              nestedLevel={nestedLevel + 1}
            />
          ))}
        </Collapse>
      )}
    </ul>
  );
}

FuseNavVerticalCollapse.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.any,
    children: PropTypes.array
  })
};
FuseNavVerticalCollapse.defaultProps = {};

const NavVerticalCollapse = withRouter(React.memo(FuseNavVerticalCollapse));

export default NavVerticalCollapse;
